import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from './Header';

const normalizeScoreLabel = (score) => String(score ?? '').trim();

const isNumericScore = (score) => {
  const value = normalizeScoreLabel(score);
  return value !== '' && !Number.isNaN(Number(value));
};

const isNaScore = (score) => normalizeScoreLabel(score).toUpperCase() === 'NA';

const isDisqualifiedScore = (score) => {
  const value = normalizeScoreLabel(score).toLowerCase();
  return value === 'disqualified' || value === 'ng';
};

const getTotalDisplay = (result) => {
  if (result?.total === undefined || result?.total === null || result?.total === '') {
    const calculatedTotal = (result?.subjects || []).reduce((sum, subj) => {
      if (isNumericScore(subj.score)) {
        return sum + Number(subj.score);
      }
      return sum;
    }, 0);
    return { type: 'numeric', value: calculatedTotal };
  }

  const totalLabel = normalizeScoreLabel(result.total);
  if (totalLabel.toUpperCase() === 'NA') {
    return { type: 'na', value: 'NA' };
  }
  if (totalLabel.toLowerCase() === 'disqualified') {
    return { type: 'disqualified', value: 'Disqualified' };
  }

  const numericTotal = Number(result.total);
  if (!Number.isNaN(numericTotal)) {
    return { type: 'numeric', value: numericTotal };
  }

  return { type: 'text', value: totalLabel };
};

const RemedialVerificationPage = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchResultByUsername = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!username) {
          setError('Invalid verification link');
          return;
        }

        let resolvedUsername = username;
        try {
          resolvedUsername = decodeURIComponent(username);
        } catch (decodeError) {
          resolvedUsername = username;
        }

        const API_KEY = process.env.REACT_APP_API_KEY || 'EAP-2024-7X9Y-2Z3W-4V5U-6T7S-8R9Q-0P1O';
        const API_URL = process.env.REACT_APP_API_URL || 'https://eap.ethernet.edu.et/api';

        const response = await fetch(`${API_URL}/remedial-exam-result`, {
          method: 'POST',
          headers: {
            'X-API-Key': API_KEY,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            username: resolvedUsername
          })
        });

        let data = null;
        try {
          data = await response.json();
        } catch (parseError) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        if (response.ok && data.status === 'success' && data.data) {
          setResult(data.data);
        } else if (response.status === 404) {
          throw new Error('Certificate not found or invalid username');
        } else if (response.status === 401) {
          throw new Error('Invalid API key');
        } else {
          throw new Error(data?.message || `Failed to fetch result (status ${response.status})`);
        }
      } catch (err) {
        console.error('Error fetching remedial result by username:', err);
        setError(err.message || 'Failed to load certificate data');
      } finally {
        setLoading(false);
      }
    };

    fetchResultByUsername();
  }, [username]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: '#6c47b6' }}></div>
            <p className="text-gray-600">Loading certificate...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center p-8 bg-white rounded-2xl shadow-lg max-w-md w-full mx-4">
            <div className="text-red-500 text-6xl mb-4">
              <i className="fa-solid fa-exclamation-triangle"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 text-white rounded-lg transition-colors"
              style={{ backgroundColor: '#6c47b6' }}
            >
              Go Back Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center p-8 bg-white rounded-2xl shadow-lg max-w-md w-full mx-4">
            <div className="text-gray-500 text-6xl mb-4">
              <i className="fa-solid fa-file-circle-question"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">No Certificate Found</h2>
            <p className="text-gray-600 mb-6">No certificate data available for this username</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 text-white rounded-lg transition-colors"
              style={{ backgroundColor: '#6c47b6' }}
            >
              Go Back Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const is2016ShortYear = String(result?.short_year || '').trim() === '2016';
  const subjectScoreSuffix = is2016ShortYear ? '/70%' : '/100';
  const score30Suffix = '/30';
  const totalScoreSuffix = is2016ShortYear ? '/100' : '';
  const showScore30Row = is2016ShortYear && result?.score_30 !== undefined && result?.score_30 !== null;
  const totalDisplay = getTotalDisplay(result);
  const total = totalDisplay.type === 'numeric' ? totalDisplay.value : null;

  let passMark = is2016ShortYear ? 50 : 250;
  if (!is2016ShortYear && result.stream_name && String(result.stream_name).toLowerCase().includes('social')) {
    passMark = 200;
  }
  if (!is2016ShortYear && result.is_blind && String(result.is_blind).toLowerCase() === 'yes') {
    passMark = 150;
  }
  const showPassFailSummary = totalDisplay.type === 'numeric';
  const passed = showPassFailSummary && total !== null ? total >= passMark : false;

  const renderSubjectScore = (score, suffix = subjectScoreSuffix) => {
    if (isDisqualifiedScore(score)) {
      return (
        <span className="inline-block bg-red-100 text-red-700 border border-red-200 px-3 py-1.5 rounded-full font-bold text-sm">
          Disqualified
        </span>
      );
    }
    if (isNaScore(score)) {
      return (
        <span className="inline-block bg-gray-100 text-gray-600 border border-gray-200 px-3 py-1.5 rounded-full font-bold text-sm">
          NA
        </span>
      );
    }
    if (!score || normalizeScoreLabel(score) === 'No Result') {
      return (
        <span className="inline-block bg-gray-100 text-gray-600 border border-gray-200 px-3 py-1.5 rounded-full font-bold text-sm">
          No Result
        </span>
      );
    }
    if (isNumericScore(score)) {
      return (
        <>
          <span className="text-lg font-semibold text-gray-800">{score}</span>
          <span className="text-gray-500">{suffix}</span>
        </>
      );
    }
    return (
      <span className="inline-block bg-gray-100 text-gray-700 border border-gray-200 px-3 py-1.5 rounded-full font-bold text-sm">
        {normalizeScoreLabel(score)}
      </span>
    );
  };

  const renderTotalScore = () => {
    if (totalDisplay.type === 'na') {
      return (
        <span className="inline-block bg-gray-100 text-gray-700 border border-gray-200 px-4 py-2 rounded-full font-bold text-lg">
          NA
        </span>
      );
    }
    if (totalDisplay.type === 'disqualified') {
      return (
        <span className="inline-block bg-red-100 text-red-700 border border-red-200 px-4 py-2 rounded-full font-bold text-lg">
          Disqualified
        </span>
      );
    }
    if (totalDisplay.type === 'numeric') {
      const value = Number.isInteger(totalDisplay.value) ? totalDisplay.value : totalDisplay.value.toFixed(2);
      return (
        <>
          <span className="text-2xl font-bold text-purple-900">{value}</span>
          {totalScoreSuffix ? <span className="text-lg text-gray-500 ml-1">{totalScoreSuffix}</span> : null}
        </>
      );
    }
    return <span className="text-2xl font-bold text-purple-900">{totalDisplay.value}</span>;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      <main className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <i className="fa-solid fa-arrow-left mr-2" style={{ color: '#6c47b6' }}></i>
            <span>Back to Home</span>
          </button>
        </div>

        <section className="bg-green-50 border border-green-200 rounded-xl p-6 mb-8">
          <div className="flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fa-solid fa-check text-2xl text-green-500"></i>
              </div>
              <h3 className="text-lg font-semibold text-green-800 mb-2">Remedial Exam Certificate Verified</h3>
              <p className="text-green-700 text-sm">
                This certificate has been successfully verified through the official Ministry of Education portal.
              </p>
            </div>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-bold flex items-center" style={{ color: '#6c47b6' }}>
            <i className="fa-solid fa-book-open mr-3 text-lg" style={{ color: '#6c47b6' }} />
            Remedial Exam Result
          </h2>
        </section>

        <section className="bg-white rounded-2xl shadow-lg mb-8 p-6 sm:p-8 border border-gray-100">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[
              { icon: 'fa-user', label: 'Username', value: result.username },
              { icon: 'fa-id-card', label: 'Full Name', value: result.full_name || 'N/A' },
              { icon: 'fa-calendar', label: 'Exam Year', value: result.short_year || 'N/A' },
              { icon: 'fa-venus-mars', label: 'Gender', value: result.gender || 'N/A' },
              { icon: 'fa-building-columns', label: 'Institution', value: result.institution_name || 'N/A' },
              { icon: 'fa-book', label: 'Stream', value: result.stream_name || 'N/A' },
            ].map(({ icon, label, value }) => (
              <div key={label} className="p-4 rounded-xl bg-gradient-to-br from-gray-50 to-purple-50">
                <div className="flex items-center mb-2">
                  <i className={`fa-solid ${icon} mr-2`} style={{ color: '#6c47b6' }} />
                  <p className="text-sm text-gray-500">{label}</p>
                </div>
                <p className="font-normal text-gray-900 break-words">{value}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white border border-purple-100 rounded-2xl shadow-sm p-6 sm:p-8 mb-4">
          <h2 className="text-2xl font-bold text-purple-800 mb-6 text-center">Subject Results</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-purple-100 to-purple-200 border-b border-purple-300">
                  <th className="text-left py-4 px-6 font-semibold text-gray-800">Subject</th>
                  <th className="text-center py-4 px-6 font-semibold text-gray-800">Score</th>
                </tr>
              </thead>
              <tbody>
                {(result.subjects || []).map((subj, idx) => (
                  <tr
                    key={subj.name || idx}
                    className="border-b border-purple-200"
                  >
                    <td className="py-4 px-6">
                      <span className="font-medium text-gray-800">{subj.name}</span>
                    </td>
                    <td className="py-4 px-6 text-center">
                      {renderSubjectScore(subj.score)}
                    </td>
                  </tr>
                ))}
                {showScore30Row && (
                  <tr className="border-b border-purple-200">
                    <td className="py-4 px-6">
                      <span className="font-medium text-gray-800">30% from the institution</span>
                    </td>
                    <td className="py-4 px-6 text-center">
                      {renderSubjectScore(result.score_30, score30Suffix)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="bg-white border border-purple-100 rounded-2xl shadow-sm p-6 sm:p-8 mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-center">
            <div className="flex items-center mb-4 sm:mb-0">
              <span className="font-semibold mr-2 text-lg text-gray-800">Total Score:</span>
              {renderTotalScore()}
              {showPassFailSummary && (
                <span className="ml-3 text-sm text-gray-500">(Pass mark: {passMark})</span>
              )}
            </div>
            {showPassFailSummary && (
              <div className="flex items-center">
                <span className="font-semibold mr-2 text-lg text-gray-800">Status:</span>
                <span className={passed
                  ? 'inline-flex items-center bg-green-100 text-green-700 border border-green-200 px-4 py-2 rounded-full font-bold text-lg shadow-sm'
                  : 'inline-flex items-center bg-red-100 text-red-700 border border-red-200 px-4 py-2 rounded-full font-bold text-lg shadow-sm'
                }>
                  {passed ? (
                    <><i className="fa-solid fa-check-circle mr-2" />Passed</>
                  ) : (
                    <><i className="fa-solid fa-times-circle mr-2" />Failed</>
                  )}
                </span>
              </div>
            )}
          </div>
        </section>

        <section className="p-6 sm:p-8 bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl border border-purple-100 shadow-md">
          <div className="flex items-start">
            <i className="fa-solid fa-circle-info text-2xl flex-shrink-0 mt-1" style={{ color: '#6c47b6' }} />
            <div className="ml-4">
              <h4 className="text-lg font-semibold mb-2" style={{ color: '#6c47b6' }}>Important Note</h4>
              <p className="text-sm" style={{ color: '#6c47b6' }}>
                This is an official verified remedial examination result from the Ministry of Education portal.
                For updates,
                <span
                  className="inline-flex items-center cursor-pointer ml-1 underline font-medium hover:opacity-80"
                  style={{ color: '#6c47b6' }}
                  onClick={() => window.open('https://t.me/moeremedialexamsupport', '_blank')}
                >
                  <i className="fa-brands fa-telegram mr-1"></i>
                  join our official Telegram channel
                </span>
                .
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default RemedialVerificationPage;
